import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import type { PDFPageProxy } from 'pdfjs-dist'
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import type { PDFDocument as PDFDocumentType } from 'pdf-lib'
import SortablePage from './components/SortablePage'
import {
  CloseIcon,
  DownloadIcon,
  FileIcon,
  GithubIcon,
  GlobeIcon,
  InstagramIcon,
  MoonIcon,
  ShieldIcon,
  SunIcon,
  UploadIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from './components/Icons'
import type { Notice, PdfPageItem, SourceDocument } from './types'

const createId = () => crypto.randomUUID()

async function pageToPreview(page: PDFPageProxy) {
  const baseViewport = page.getViewport({ scale: 1 })
  const scale = Math.min(1.2, 360 / baseViewport.width)
  const viewport = page.getViewport({ scale })
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d', { alpha: false })

  if (!context) throw new Error('미리보기를 만들 수 없습니다.')

  const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5)
  canvas.width = Math.floor(viewport.width * pixelRatio)
  canvas.height = Math.floor(viewport.height * pixelRatio)
  canvas.style.width = `${Math.floor(viewport.width)}px`
  canvas.style.height = `${Math.floor(viewport.height)}px`

  await page.render({
    canvas,
    canvasContext: context,
    viewport,
    transform: pixelRatio === 1 ? undefined : [pixelRatio, 0, 0, pixelRatio, 0, 0],
  }).promise

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new Error('미리보기 변환에 실패했습니다.'))),
      'image/jpeg',
      0.82,
    )
  })

  canvas.width = 0
  canvas.height = 0
  return URL.createObjectURL(blob)
}

async function pageToDetailedPreview(bytes: Uint8Array, pageIndex: number) {
  const { GlobalWorkerOptions, getDocument } = await import('pdfjs-dist')
  GlobalWorkerOptions.workerSrc = pdfWorker
  const loadingTask = getDocument({ data: bytes.slice() })
  const pdf = await loadingTask.promise

  try {
    const page = await pdf.getPage(pageIndex + 1)
    const baseViewport = page.getViewport({ scale: 1 })
    const longestEdge = Math.max(baseViewport.width, baseViewport.height)
    const scale = Math.max(0.35, Math.min(3, 2000 / longestEdge))
    const viewport = page.getViewport({ scale })
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d', { alpha: false })

    if (!context) throw new Error('고해상도 미리보기를 만들 수 없습니다.')

    canvas.width = Math.max(1, Math.floor(viewport.width))
    canvas.height = Math.max(1, Math.floor(viewport.height))

    await page.render({ canvas, canvasContext: context, viewport }).promise

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) => (result ? resolve(result) : reject(new Error('고해상도 미리보기 변환에 실패했습니다.'))),
        'image/jpeg',
        0.92,
      )
    })

    canvas.width = 0
    canvas.height = 0
    page.cleanup()
    return URL.createObjectURL(blob)
  } finally {
    await pdf.destroy()
  }
}

function getFriendlyError(error: unknown, fileName: string) {
  const message = error instanceof Error ? error.message : ''
  if (/password/i.test(message)) return `${fileName}: 암호가 설정된 PDF는 불러올 수 없습니다.`
  if (/invalid|format|pdf/i.test(message)) return `${fileName}: 올바른 PDF 파일인지 확인해 주세요.`
  return `${fileName}: 파일을 처리하지 못했습니다.`
}

export default function App() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    const saved = localStorage.getItem('zozinpdf-theme')
    return saved === 'light' ? 'light' : 'dark'
  })
  const [pages, setPages] = useState<PdfPageItem[]>([])
  const [documents, setDocuments] = useState<Record<string, SourceDocument>>({})
  const [activeUploads, setActiveUploads] = useState(0)
  const [isDraggingFiles, setIsDraggingFiles] = useState(false)
  const [isMerging, setIsMerging] = useState(false)
  const [previewPage, setPreviewPage] = useState<PdfPageItem | null>(null)
  const [detailedPreviewUrl, setDetailedPreviewUrl] = useState<string | null>(null)
  const [isDetailedPreviewLoading, setIsDetailedPreviewLoading] = useState(false)
  const [detailedPreviewFailed, setDetailedPreviewFailed] = useState(false)
  const [previewZoom, setPreviewZoom] = useState(1)
  const [isPreviewPanning, setIsPreviewPanning] = useState(false)
  const [notice, setNotice] = useState<Notice>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const previewCloseRef = useRef<HTMLButtonElement>(null)
  const previewCanvasRef = useRef<HTMLDivElement>(null)
  const previewZoomRef = useRef(1)
  const previewPointersRef = useRef(new Map<number, { x: number; y: number }>())
  const previewPanRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    scrollLeft: number
    scrollTop: number
  } | null>(null)
  const previewPinchRef = useRef<{ distance: number; zoom: number } | null>(null)
  const previewUrlsRef = useRef(new Set<string>())
  const noticeTimerRef = useRef<number | null>(null)

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    document.querySelector('meta[name="theme-color"]')?.setAttribute(
      'content',
      theme === 'dark' ? '#080b10' : '#f7faff',
    )
    localStorage.setItem('zozinpdf-theme', theme)
  }, [theme])

  useEffect(() => {
    const previewUrls = previewUrlsRef.current
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url))
      if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (!previewPage) return

    const previousOverflow = document.body.style.overflow
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPreviewPage(null)
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleEscape)
    previewCloseRef.current?.focus()

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleEscape)
    }
  }, [previewPage])

  const previewSource = previewPage ? documents[previewPage.documentId] : undefined

  useEffect(() => {
    if (!previewPage || !previewSource) return

    let disposed = false
    let generatedUrl: string | null = null

    void pageToDetailedPreview(previewSource.bytes, previewPage.sourcePageIndex)
      .then((url) => {
        if (disposed) {
          URL.revokeObjectURL(url)
          return
        }
        generatedUrl = url
        setDetailedPreviewUrl(url)
      })
      .catch(() => {
        if (!disposed) setDetailedPreviewFailed(true)
      })
      .finally(() => {
        if (!disposed) setIsDetailedPreviewLoading(false)
      })

    return () => {
      disposed = true
      if (generatedUrl) URL.revokeObjectURL(generatedUrl)
    }
  }, [previewPage, previewSource])

  const applyPreviewZoom = useCallback((value: number, clientX?: number, clientY?: number) => {
    const canvas = previewCanvasRef.current
    const previousZoom = previewZoomRef.current
    const nextZoom = Math.min(3, Math.max(1, value))

    if (Math.abs(nextZoom - previousZoom) < 0.001) return

    let offsetX = 0
    let offsetY = 0
    let contentX = 0
    let contentY = 0

    if (canvas) {
      const rect = canvas.getBoundingClientRect()
      offsetX = clientX === undefined ? canvas.clientWidth / 2 : clientX - rect.left
      offsetY = clientY === undefined ? canvas.clientHeight / 2 : clientY - rect.top
      contentX = (canvas.scrollLeft + offsetX) / previousZoom
      contentY = (canvas.scrollTop + offsetY) / previousZoom
    }

    previewZoomRef.current = nextZoom
    setPreviewZoom(nextZoom)

    if (canvas) {
      window.requestAnimationFrame(() => {
        canvas.scrollLeft = contentX * nextZoom - offsetX
        canvas.scrollTop = contentY * nextZoom - offsetY
      })
    }
  }, [])

  const openPreview = useCallback((page: PdfPageItem) => {
    previewPointersRef.current.clear()
    previewPanRef.current = null
    previewPinchRef.current = null
    previewZoomRef.current = 1
    setDetailedPreviewUrl(null)
    setDetailedPreviewFailed(false)
    setIsDetailedPreviewLoading(true)
    setPreviewZoom(1)
    setIsPreviewPanning(false)
    setPreviewPage(page)
  }, [])

  const resetPreviewGesture = useCallback(() => {
    previewPointersRef.current.clear()
    previewPanRef.current = null
    previewPinchRef.current = null
    setIsPreviewPanning(false)
  }, [])

  const handlePreviewWheel = useCallback((event: WheelEvent) => {
    event.preventDefault()
    const zoomFactor = Math.exp(-event.deltaY * 0.002)
    applyPreviewZoom(previewZoomRef.current * zoomFactor, event.clientX, event.clientY)
  }, [applyPreviewZoom])

  useEffect(() => {
    const canvas = previewCanvasRef.current
    if (!previewPage || !canvas) return

    canvas.addEventListener('wheel', handlePreviewWheel, { passive: false })
    return () => canvas.removeEventListener('wheel', handlePreviewWheel)
  }, [handlePreviewWheel, previewPage])

  const handlePreviewPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return

    event.currentTarget.setPointerCapture(event.pointerId)
    previewPointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY })

    if (previewPointersRef.current.size === 1) {
      previewPanRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        scrollLeft: event.currentTarget.scrollLeft,
        scrollTop: event.currentTarget.scrollTop,
      }
      if (previewZoomRef.current > 1) setIsPreviewPanning(true)
      return
    }

    const [first, second] = Array.from(previewPointersRef.current.values())
    previewPinchRef.current = {
      distance: Math.hypot(second.x - first.x, second.y - first.y),
      zoom: previewZoomRef.current,
    }
    previewPanRef.current = null
    setIsPreviewPanning(false)
  }, [])

  const handlePreviewPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!previewPointersRef.current.has(event.pointerId)) return

    previewPointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY })

    if (previewPointersRef.current.size >= 2) {
      const [first, second] = Array.from(previewPointersRef.current.values())
      const distance = Math.hypot(second.x - first.x, second.y - first.y)
      const pinch = previewPinchRef.current
      if (!pinch || pinch.distance <= 0) return

      applyPreviewZoom(
        pinch.zoom * (distance / pinch.distance),
        (first.x + second.x) / 2,
        (first.y + second.y) / 2,
      )
      return
    }

    const pan = previewPanRef.current
    if (!pan || pan.pointerId !== event.pointerId || previewZoomRef.current <= 1) return

    event.currentTarget.scrollLeft = pan.scrollLeft - (event.clientX - pan.startX)
    event.currentTarget.scrollTop = pan.scrollTop - (event.clientY - pan.startY)
  }, [applyPreviewZoom])

  const handlePreviewPointerEnd = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    previewPointersRef.current.delete(event.pointerId)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    const remaining = Array.from(previewPointersRef.current.entries())
    if (remaining.length === 1) {
      const [pointerId, point] = remaining[0]
      previewPanRef.current = {
        pointerId,
        startX: point.x,
        startY: point.y,
        scrollLeft: event.currentTarget.scrollLeft,
        scrollTop: event.currentTarget.scrollTop,
      }
      previewPinchRef.current = null
      setIsPreviewPanning(previewZoomRef.current > 1)
      return
    }

    resetPreviewGesture()
  }, [resetPreviewGesture])

  const showNotice = useCallback((nextNotice: NonNullable<Notice>) => {
    setNotice(nextNotice)
    if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current)
    noticeTimerRef.current = window.setTimeout(() => setNotice(null), 4200)
  }, [])

  const importSingleFile = useCallback(async (file: File) => {
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      throw new Error(`${file.name}: PDF 파일만 업로드할 수 있습니다.`)
    }

    const documentId = createId()
    const bytes = new Uint8Array(await file.arrayBuffer())
    const { GlobalWorkerOptions, getDocument } = await import('pdfjs-dist')
    GlobalWorkerOptions.workerSrc = pdfWorker
    const loadingTask = getDocument({ data: bytes.slice() })
    const pdf = await loadingTask.promise
    const createdPreviewUrls: string[] = []
    const nextDocument: SourceDocument = {
      id: documentId,
      name: file.name,
      bytes,
      pageCount: pdf.numPages,
    }

    setDocuments((current) => ({ ...current, [documentId]: nextDocument }))

    try {
      const nextPages: PdfPageItem[] = []
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
        const pdfPage = await pdf.getPage(pageNumber)
        const previewUrl = await pageToPreview(pdfPage)
        createdPreviewUrls.push(previewUrl)
        previewUrlsRef.current.add(previewUrl)
        nextPages.push({
          id: createId(),
          documentId,
          sourcePageIndex: pageNumber - 1,
          fileName: file.name,
          previewUrl,
        })
        pdfPage.cleanup()
      }
      setPages((current) => [...current, ...nextPages])
      return pdf.numPages
    } catch (error) {
      createdPreviewUrls.forEach((url) => {
        URL.revokeObjectURL(url)
        previewUrlsRef.current.delete(url)
      })
      setDocuments((current) => {
        const next = { ...current }
        delete next[documentId]
        return next
      })
      throw error
    } finally {
      await pdf.destroy()
    }
  }, [])

  const handleFiles = useCallback(async (fileList: FileList | File[]) => {
    const files = Array.from(fileList)
    if (!files.length) return

    setActiveUploads((count) => count + files.length)
    let addedPages = 0
    const errors: string[] = []

    await Promise.all(
      files.map(async (file) => {
        try {
          addedPages += await importSingleFile(file)
        } catch (error) {
          const directMessage = error instanceof Error && error.message.startsWith(`${file.name}:`)
          errors.push(directMessage ? (error as Error).message : getFriendlyError(error, file.name))
        } finally {
          setActiveUploads((count) => Math.max(0, count - 1))
        }
      }),
    )

    if (errors.length) {
      showNotice({ type: 'error', message: errors.join(' ') })
    } else {
      showNotice({ type: 'success', message: `${files.length}개 파일, ${addedPages}페이지를 추가했습니다.` })
    }
  }, [importSingleFile, showNotice])

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    setPages((current) => {
      const oldIndex = current.findIndex((page) => page.id === active.id)
      const newIndex = current.findIndex((page) => page.id === over.id)
      return arrayMove(current, oldIndex, newIndex)
    })
  }

  const movePage = (id: string, direction: -1 | 1) => {
    setPages((current) => {
      const index = current.findIndex((page) => page.id === id)
      const nextIndex = index + direction
      if (index < 0 || nextIndex < 0 || nextIndex >= current.length) return current
      return arrayMove(current, index, nextIndex)
    })
  }

  const removePage = (id: string) => {
    if (previewPage?.id === id) setPreviewPage(null)
    setPages((current) => {
      const target = current.find((page) => page.id === id)
      if (target) {
        URL.revokeObjectURL(target.previewUrl)
        previewUrlsRef.current.delete(target.previewUrl)
      }
      return current.filter((page) => page.id !== id)
    })
  }

  const clearPages = () => {
    setPreviewPage(null)
    pages.forEach((page) => {
      URL.revokeObjectURL(page.previewUrl)
      previewUrlsRef.current.delete(page.previewUrl)
    })
    setPages([])
    setDocuments({})
  }

  const mergeAndDownload = async () => {
    if (!pages.length || isMerging) return
    setIsMerging(true)
    try {
      const { PDFDocument } = await import('pdf-lib')
      const output = await PDFDocument.create()
      const loadedSources = new Map<string, PDFDocumentType>()

      output.setTitle('zozinpdf')
      output.setCreator('zozinpdf')
      output.setProducer('zozinpdf')

      for (const page of pages) {
        let source = loadedSources.get(page.documentId)
        if (!source) {
          const sourceDocument = documents[page.documentId]
          if (!sourceDocument) throw new Error('원본 파일을 찾을 수 없습니다.')
          source = await PDFDocument.load(sourceDocument.bytes)
          loadedSources.set(page.documentId, source)
        }
        const [copiedPage] = await output.copyPages(source, [page.sourcePageIndex])
        output.addPage(copiedPage)
      }

      const outputBytes = await output.save({ useObjectStreams: true })
      const blob = new Blob([new Uint8Array(outputBytes)], { type: 'application/pdf' })
      const downloadUrl = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = downloadUrl
      anchor.download = 'zozinpdf.pdf'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000)
      showNotice({ type: 'success', message: 'zozinpdf.pdf 다운로드를 시작했습니다.' })
    } catch (error) {
      console.error(error)
      showNotice({ type: 'error', message: 'PDF를 병합하지 못했습니다. 파일을 다시 확인해 주세요.' })
    } finally {
      setIsMerging(false)
    }
  }

  const sourceCount = new Set(pages.map((page) => page.documentId)).size

  return (
    <div className="app-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="zozinpdf 홈">
          <span className="brand__mark"><FileIcon /></span>
          <span>zozin<span>pdf</span></span>
        </a>
        <div className="header-actions">
          <span className="privacy-pill"><ShieldIcon /> 파일은 브라우저 안에서만 처리돼요</span>
          <button
            className="theme-toggle"
            type="button"
            onClick={() => setTheme((current) => (current === 'dark' ? 'light' : 'dark'))}
            aria-label={theme === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
            title={theme === 'dark' ? '라이트 모드' : '다크 모드'}
          >
            <SunIcon className="theme-toggle__sun" />
            <MoonIcon className="theme-toggle__moon" />
          </button>
        </div>
      </header>

      <main id="top">
        <input
          ref={inputRef}
          className="file-input"
          type="file"
          accept="application/pdf,.pdf"
          multiple
          onChange={(event) => {
            if (event.target.files) void handleFiles(event.target.files)
            event.target.value = ''
          }}
        />

        <section className="hero" aria-labelledby="hero-title">
          <div className="hero__copy">
            <p className="eyebrow"><span /> PRIVATE PDF WORKSPACE</p>
            <h1 id="hero-title">흩어진 PDF를<br /><em>한 흐름</em>으로.</h1>
            <p className="hero__description">
              여러 PDF의 페이지를 한눈에 확인하고, 원하는 순서로 정리해<br className="desktop-break" />
              단 하나의 문서로 완성하세요.
            </p>
          </div>
        </section>

        <section
          className={`workspace${isDraggingFiles ? ' is-over' : ''}`}
          aria-labelledby="workspace-title"
          onDragEnter={(event) => { event.preventDefault(); setIsDraggingFiles(true) }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node)) setIsDraggingFiles(false)
          }}
          onDrop={(event) => {
            event.preventDefault()
            setIsDraggingFiles(false)
            void handleFiles(event.dataTransfer.files)
          }}
        >
          <div className="section-heading">
            <div>
              <p className="section-index">01 — ARRANGE</p>
              <h2 id="workspace-title">페이지 작업대</h2>
              <p>카드를 드래그해 최종 PDF의 페이지 순서를 정하세요.</p>
              {activeUploads > 0 && (
                <span className="workspace-upload-status" role="status">
                  <i /> {activeUploads}개 파일 처리 중
                </span>
              )}
            </div>
            <div className="workspace-actions">
              <div className="workspace-stats" aria-label="현재 작업 통계">
                <span><strong>{sourceCount}</strong> 파일</span>
                <span><strong>{pages.length}</strong> 페이지</span>
              </div>
              {pages.length > 0 && (
                <button className="text-button danger" type="button" onClick={clearPages}>모두 비우기</button>
              )}
              <button className="outline-button add-button" type="button" onClick={() => inputRef.current?.click()}>
                <UploadIcon /> PDF 추가
              </button>
            </div>
          </div>

          {pages.length === 0 ? (
            <button className="empty-workspace" type="button" onClick={() => inputRef.current?.click()}>
              <span className="empty-workspace__sheets" aria-hidden="true">
                <i /><i /><i><FileIcon /></i>
              </span>
              <strong>{activeUploads ? '페이지 미리보기를 만들고 있어요' : 'PDF를 작업대에 놓으세요'}</strong>
              <span>{activeUploads ? '파일 크기에 따라 잠시 걸릴 수 있습니다.' : '여기로 드래그하거나 눌러서 여러 PDF를 선택할 수 있어요.'}</span>
            </button>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={pages.map((page) => page.id)} strategy={rectSortingStrategy}>
                <div className="page-grid">
                  {pages.map((page, index) => (
                    <SortablePage
                      key={page.id}
                      page={page}
                      position={index}
                      total={pages.length}
                      onMove={movePage}
                      onRemove={removePage}
                      onOpen={openPreview}
                    />
                  ))}
                  <button className="page-add-card" type="button" onClick={() => inputRef.current?.click()}>
                    <span><UploadIcon /></span>
                    <strong>PDF 추가</strong>
                    <small>작업 중에도 추가 가능</small>
                  </button>
                </div>
              </SortableContext>
            </DndContext>
          )}
        </section>

        <section className="finish-section" aria-labelledby="finish-title">
          <div>
            <p className="section-index">02 — MERGE</p>
            <h2 id="finish-title">순서가 맞다면, 이제 하나로.</h2>
            <p>현재 작업대의 왼쪽 위부터 오른쪽 아래 순서로 병합됩니다.</p>
          </div>
          <button
            className="download-button"
            type="button"
            onClick={() => void mergeAndDownload()}
            disabled={!pages.length || isMerging}
          >
            <span><DownloadIcon /></span>
            <span>
              <strong>{isMerging ? 'PDF를 만드는 중...' : 'zozinpdf.pdf 다운로드'}</strong>
              <small>{pages.length ? `${pages.length}개 페이지를 하나의 파일로` : '먼저 PDF를 추가해 주세요'}</small>
            </span>
          </button>
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-brand">
          <span className="brand__mark"><FileIcon /></span>
          <div><strong>zozinpdf</strong><span>Make documents flow.</span></div>
        </div>
        <p>© {new Date().getFullYear()} ZOZINDEV. 파일을 저장하거나 수집하지 않습니다.</p>
        <nav aria-label="외부 링크">
          <a href="https://github.com/zozindev/zozinpdf" target="_blank" rel="noreferrer" aria-label="GitHub 저장소"><GithubIcon /><span>GitHub</span></a>
          <a href="https://www.instagram.com/zozindev/" target="_blank" rel="noreferrer" aria-label="Instagram"><InstagramIcon /><span>Instagram</span></a>
          <a href="https://zozindev.com" target="_blank" rel="noreferrer" aria-label="개인 블로그"><GlobeIcon /><span>Blog</span></a>
        </nav>
      </footer>

      {previewPage && (
        <div
          className="page-preview-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setPreviewPage(null)
          }}
        >
          <section
            className="page-preview-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="page-preview-title"
          >
            <header className="page-preview-modal__header">
              <div>
                <span>PAGE PREVIEW</span>
                <strong id="page-preview-title">{previewPage.fileName}</strong>
              </div>
              <button
                ref={previewCloseRef}
                type="button"
                onClick={() => setPreviewPage(null)}
                aria-label="페이지 미리보기 닫기"
                title="닫기 (ESC)"
              >
                <CloseIcon />
              </button>
            </header>
            <div
              ref={previewCanvasRef}
              className={`page-preview-modal__canvas${previewZoom > 1 ? ' is-zoomed' : ''}${isPreviewPanning ? ' is-panning' : ''}`}
              onPointerDown={handlePreviewPointerDown}
              onPointerMove={handlePreviewPointerMove}
              onPointerUp={handlePreviewPointerEnd}
              onPointerCancel={handlePreviewPointerEnd}
            >
              <div
                className="page-preview-modal__stage"
                style={{ width: `${previewZoom * 100}%`, height: `${previewZoom * 100}%` }}
              >
                <img
                  src={detailedPreviewUrl ?? previewPage.previewUrl}
                  alt={`${previewPage.fileName}의 ${previewPage.sourcePageIndex + 1}페이지 크게 보기`}
                  draggable={false}
                />
              </div>
              {isDetailedPreviewLoading && (
                <div className="page-preview-modal__render-status" role="status">
                  <i /> 고해상도 준비 중
                </div>
              )}
              {detailedPreviewFailed && (
                <div className="page-preview-modal__render-status is-warning" role="status">
                  기본 미리보기로 표시 중
                </div>
              )}
            </div>
            <div className="page-preview-modal__toolbar" role="group" aria-label="페이지 확대 도구">
              <button
                type="button"
                onClick={() => applyPreviewZoom(previewZoomRef.current - 0.25)}
                disabled={previewZoom <= 1}
                aria-label="축소"
                title="축소"
              >
                <ZoomOutIcon />
              </button>
              <output aria-live="polite">{Math.round(previewZoom * 100)}%</output>
              <button
                type="button"
                onClick={() => applyPreviewZoom(previewZoomRef.current + 0.25)}
                disabled={previewZoom >= 3}
                aria-label="확대"
                title="확대"
              >
                <ZoomInIcon />
              </button>
              <button
                className="page-preview-modal__fit-button"
                type="button"
                onClick={() => applyPreviewZoom(1)}
                disabled={previewZoom <= 1}
              >
                맞춤
              </button>
            </div>
            <footer className="page-preview-modal__footer">
              <span>원본 {previewPage.sourcePageIndex + 1} 페이지</span>
              <span>{detailedPreviewUrl ? '고해상도' : '기본 미리보기'} · 휠 또는 핀치로 확대</span>
            </footer>
          </section>
        </div>
      )}

      {notice && (
        <div className={`notice notice--${notice.type}`} role="status" aria-live="polite">
          <span>{notice.message}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="알림 닫기"><CloseIcon /></button>
        </div>
      )}
    </div>
  )
}
